// SIMPLE WORKING Headshot Fetcher - Supports NBA + NFL
const headshotCache = new Map();

// Test with known working NBA player IDs
const KNOWN_NBA_PLAYER_IDS = {
  'LeBron James': '2544',
  'Stephen Curry': '201939',
  'Kevin Durant': '201142',
  'Giannis Antetokounmpo': '203507',
  'Jayson Tatum': '1628369',
  'Jaylen Brown': '1627759',
  'Luka Doncic': '1629029',
  'Joel Embiid': '203954',
  'Nikola Jokic': '203999',
  'Anthony Davis': '203076',
  'Damian Lillard': '203081',
  'Jimmy Butler': '202710',
  'Kawhi Leonard': '202695',
  'Paul George': '202331',
  'Russell Westbrook': '201566',
  'Chris Paul': '101108',
  'Kyrie Irving': '202681',
  'James Harden': '201935',
  'Klay Thompson': '202691',
  'Draymond Green': '203110',
  'Trae Young': '1629027',
  'Donovan Mitchell': '1628378',
  'Devin Booker': '1626164',
  'Bradley Beal': '203078',
  'Jalen Brunson': '1628973',
  'Julius Randle': '203944',
  'Mikal Bridges': '1628969',
  'Cameron Johnson': '1629661',
  'Cade Cunningham': '1630595',
  'Jalen Green': '1630224',
  'Alperen Sengun': '1630578',
  'Anfernee Simons': '1629014',
  'Bam Adebayo': '1628389',
  'Ja Morant': '1629630',
  'Jaren Jackson Jr.': '1628991',
  'Brook Lopez': '201572',
  'Pascal Siakam': '1627783',
  'Scottie Barnes': '1630567',
  'Anthony Edwards': '1630162',
  'Karl-Anthony Towns': '1626157',
  'D\'Angelo Russell': '1626156',
  'Dejounte Murray': '1627749',
  'Zion Williamson': '1629627',
  'Brandon Ingram': '1627742',
  'CJ McCollum': '203468',
  'De\'Aaron Fox': '1628368',
  'Domantas Sabonis': '1627734',
  'Malik Monk': '1628370',
  'Kristaps Porzingis': '204001',
  'Cam Thomas': '1630560',
  'Bruce Brown': '1628971',
  'Sam Merrill': '1630241',
  'Tyler Herro': '1629639',
  'Terry Rozier': '1626179',
  'PJ Washington': '1629023',
  'Brandon Miller': '1641705',
  'LaMelo Ball': '1630163',
  'Miles Bridges': '1628970',
  'Gordon Hayward': '202330',
  'Cody Martin': '1628998',
  'Nick Richards': '1630208',
  'Mark Williams': '1631109',
  'Nathan Mensah': '1641710',
  'Tre Mann': '1630544',
  'Vasilije Micic': '1631368',
  'Davis Bertans': '202722',
  'Aleksa Avramovic': '1641711',
  'Aaron Nesmith': '1630174',
  'Obi Toppin': '1630167',
  'T.J. McConnell': '204456',
  'Jalen Smith': '1630188',
  'Isaiah Jackson': '1630543',
  'Ben Sheppard': '1641712',
  'Andrew Nembhard': '1630610',
  'Jarace Walker': '1630567',
  'Kendall Brown': '1631112',
  'Isaiah Wong': '1641714',
  'Oscar Tshiebwe': '1641715',
  'James Johnson': '201949',
  'Tyrese Haliburton': '1630169',
  'Myles Turner': '1626167',
  'Bennedict Mathurin': '1630177',
  'Desmond Bane': '1630179',
  // New players from latest picks
  'Moussa Diabate': '1631217',
  'Davion Mitchell': '1630558',
  'Norman Powell': '1626181',
  'Nikola Jovic': '1631107',
  'Ryan Rollins': '1631157',
  // Players from current JSON
  'Immanuel Quickley': '1630193',
  'Payton Pritchard': '1630202',
  'Josh Minott': '1631169',
  'Josh Giddey': '1630581',
  'Max Christie': '1631108',
  'Austin Reaves': '1630559',
  'Jake LaRavia': '1631222',
  'Jarred Vanderbilt': '1629020',
  'Jock Landale': '1629111',
  'Grayson Allen': '1628960',
  'Royce O\'Neale': '1626220',
  // Additional players from latest JSON
  'DeMar DeRozan': '201942',
  'Zach LaVine': '203897',
  'Deni Avdija': '1630166',
  'Jusuf Nurkic': '203994',
  'Rudy Gobert': '203497',
  // Players from current picks
  'Nickeil Alexander-Walker': '1629638',
  'Jay Huff': '1630643',
  'Jonas Valanciunas': '202685',
  'Collin Gillespie': '1631221',
  'Khris Middleton': '203114',
  'Corey Kispert': '1630557'
};

// Get NBA headshot URL using player ID
const getNBAHeadshotByPlayerId = (playerId) => {
  return `https://cdn.nba.com/headshots/nba/latest/260x190/${playerId}.png`;
};

// Fetch NFL headshot via ESPN search API
const fetchNFLHeadshot = async (playerName) => {
  try {
    const searchQuery = encodeURIComponent(playerName);
    const response = await fetch(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/athletes?search=${searchQuery}`);
    const data = await response.json();

    if (data?.athletes?.length) {
      const athlete = data.athletes[0];
      if (athlete?.headshot?.href) {
        return athlete.headshot.href.replace('&h=300&w=300', '&h=400&w=400');
      }
    }
  } catch (error) {
    console.warn(`NFL headshot lookup failed for ${playerName}:`, error);
  }
  return null;
};

// Main function to get player headshot
export const getDynamicPlayerHeadshot = async (playerName, playerId = null, league = 'nba', espnId = null) => {
  const cacheKey = `${league}:${playerName}`;

  // Check cache first
  if (headshotCache.has(cacheKey)) {
    return headshotCache.get(cacheKey);
  }
  
  if (league === 'nba') {
    console.log(`🔍 Getting NBA headshot for: ${playerName}`);
    
    // Use provided player_id if available, otherwise look up in known players
    let idToUse = playerId;
    if (!idToUse) {
      idToUse = KNOWN_NBA_PLAYER_IDS[playerName];
    }
    
    if (idToUse) {
      const headshotUrl = getNBAHeadshotByPlayerId(idToUse);
      console.log(`📸 Found NBA headshot: ${headshotUrl}`);
      headshotCache.set(cacheKey, headshotUrl);
      return headshotUrl;
    }
    
    console.log(`❌ NBA player not found in known players: ${playerName}`);
    headshotCache.set(cacheKey, null);
    return null;
  }

  if (league === 'nfl') {
    console.log(`🏈 Getting NFL headshot for: ${playerName}`);

    let idToUse = null;
    if (espnId && !isNaN(Number(espnId))) {
      idToUse = espnId;
    }

    if (!idToUse && playerId && !isNaN(Number(playerId))) {
      idToUse = playerId;
    }

    if (!idToUse) {
      const fetched = await fetchNFLHeadshot(playerName);
      if (fetched) {
        console.log(`📸 Found NFL headshot via search: ${fetched}`);
        headshotCache.set(cacheKey, fetched);
        return fetched;
      }
      console.log(`❌ NFL headshot not found for ${playerName}`);
      headshotCache.set(cacheKey, null);
      return null;
    }

    const headshotUrl = `https://a.espncdn.com/i/headshots/nfl/players/full/${idToUse}.png`;
    headshotCache.set(cacheKey, headshotUrl);
    return headshotUrl;
  }

  // Default fallback
  headshotCache.set(cacheKey, null);
  return null;
};

export const getPlayerData = (playerName) => {
    return {
    name: playerName,
    headshot: null
  };
};

export const getPlayerHeadshot = getDynamicPlayerHeadshot;